<template>
  <div>
    <div class="mb-6">
      <p class="h6 pa-0 mb-0">
        {{ $t('organization_settings.webhooks') }}
      </p>
      <p class="mt-2 pa-0 grey--text text--darken-2">
        {{ $t('organization_settings.webhook_list_subtitle') }}
      </p>
      <p
        class="mt-2 pa-0 grey--text text--darken-2"
        v-html="
          $sanitize(
            $t('organization_settings.webhook_form_developers_site_copy'),
            { allowedTags: ['a'] }
          )
        "
      />
    </div>
    <v-row class="px-2 py-4 mb-3">
      <v-col cols="12">
        <SearchInput class="mr-4" :value="searchTerms" @change="handleSearch" />
      </v-col>
    </v-row>
    <div v-if="hasItems || isLoading">
      <v-data-table
        class="mt-8"
        data-private
        :footer-props="footerOptions"
        :headers="headers"
        :items="webhooks"
        :items-per-page="itemsPerPage"
        :item-class="rowsClass"
        :loading="isLoading"
        :options.sync="options"
        :page="currentPage"
        :server-items-length="totalItems"
        @click:row="$emit('editWebhook', $event)"
      >
        <template v-slot:item.enabled="{ item }">
          <v-tooltip top>
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                class="cursor-default"
                small
                v-on="on"
                v-bind="attrs"
                :color="getEnableStatusColor(item.enabled)"
                >mdi-brightness-1</v-icon
              >
            </template>

            <span>{{ getEnableStatusText(item.enabled) }}</span>
          </v-tooltip>
        </template>

        <template v-slot:item.name="{ item }">
          <span>{{ item.name }}</span>
        </template>

        <template v-slot:item.url="{ item }">
          <span :title="item.url">{{ shortenURL(item.url) }}</span>
        </template>

        <template v-slot:item.createdAt="{ item }">
          <span :title="item.createdAt">{{ humanDate(item.createdAt) }}</span>
        </template>

        <template v-slot:item.actions="{ item }">
          <v-icon class="mr-2" @click="$emit('editWebhook', item)">
            mdi-pencil-outline
          </v-icon>

          <v-icon @click.stop="toggleDeleteConfirmation(item.id)">
            mdi-delete-outline
          </v-icon>
        </template>
      </v-data-table>

      <div class="my-6 text-right">
        <Button
          color="primary"
          size="large"
          :text="$t('organization_settings.webhooks_add_new')"
          @click="$emit('addWebhook')"
        />
      </div>
    </div>

    <div v-else class="d-flex justify-center">
      <div class="d-flex flex-column align-center empty-state">
        <img :src="emptyStateImage" alt="empty_state" />
        <template v-if="emptySearchResults">
          <p class="subtitle2 my-4 text-center">
            {{
              $t('organization_settings.webhooks_empty_search_results_title')
            }}
          </p>
          <p class="grey--text text--darken-2 text-center">
            {{
              $t('organization_settings.webhooks_empty_search_results_subtitle')
            }}
          </p>
        </template>
        <template v-else>
          <p class="subtitle2 my-4 text-center">
            {{ $t('organization_settings.webhooks_empty_state_title') }}
          </p>

          <p class="grey--text text--darken-2 text-center">
            {{ $t('organization_settings.webhooks_empty_state_subtitle') }}
            <a
              :href="$t('organization_settings.webhooks_help_article')"
              target="__blank"
            >
              {{ $t('common.learn_more') }}
            </a>
          </p>

          <Button
            class="my-6 empty-state__button"
            color="primary"
            size="large"
            data-tourid="webhooks-button-addWebhook"
            :text="$t('organization_settings.webhooks_add_new')"
            @click="$emit('addWebhook')"
          />
        </template>
      </div>
    </div>
    <DeleteConfirmationModal
      :show="isDeleteConfirmationVisible"
      :isLoading="isDeleting"
      :title="$t('organization_settings.webhooks_delete_title')"
      :text="$t('organization_settings.webhooks_delete_description')"
      @cancel="toggleDeleteConfirmation"
      @delete="handleWebhookDeleted"
    />
  </div>
</template>

<script>
import _ from 'lodash'
import { mapActions } from 'vuex'

import mixin from '@/mixins/mixin.vue'
import { utils } from 'frontend-js-shared'

import DeleteConfirmationModal from '@/components/Modals/DeleteConfirmationModal.vue'
import Button from '@/components/UI/Button'
import SearchInput from '@/components/Core/SearchInput.vue'

import emptyStateImage from '@/assets/images/empty-states/webhooks-empty-state.svg'

const MAX_URL_CHARACTERS = 40
const INITIAL_ITEMS_PER_PAGE = 20

export default {
  name: 'WebhookList',

  mixins: [mixin],

  components: {
    DeleteConfirmationModal,
    Button,
    SearchInput,
  },

  props: {
    organizationId: {
      required: true,
      type: String,
    },
  },

  data() {
    return {
      emptyStateImage,
      webhooks: [],
      headers: [
        {
          text: '',
          align: 'center',
          sortable: false,
          value: 'enabled',
          width: 20,
        },
        {
          text: this.$t('common.name'),
          align: 'start',
          sortable: true,
          value: 'name',
        },
        {
          text: this.$t('organization_settings.webhooks_url'),
          align: 'start',
          sortable: false,
          value: 'url',
        },
        {
          text: this.$t('organization_settings.webhooks_creation_date'),
          align: 'start',
          sortable: true,
          value: 'createdAt',
          width: 130,
        },
        {
          text: _.capitalize(this.$t('common.action_other')),
          sortable: false,
          value: 'actions',
          width: 80,
        },
      ],
      footerOptions: {
        itemsPerPageOptions: [20, 50, 100],
      },
      totalItems: 0,
      currentPage: 1,
      itemsPerPage: INITIAL_ITEMS_PER_PAGE,
      isLoading: false,
      isDeleteConfirmationVisible: false,
      webhookIdToDelete: '',
      isDeleting: false,
      searchTerms: '',
      sortBy: 'createdAt',
      sortOrder: 'desc',
    }
  },

  computed: {
    hasItems() {
      return !_.isEmpty(this.webhooks)
    },

    options: {
      get() {
        return {
          page: this.currentPage,
          itemsPerPage: this.itemsPerPage,
        }
      },

      set(event) {
        this.currentPage = event.page
        this.itemsPerPage = event.itemsPerPage
        this.sortBy = 'updatedAt'
        this.sortOrder = 'desc'
        if (!_.isEmpty(event.sortDesc)) {
          this.sortBy = event.sortBy[0]
          this.sortOrder = event.sortDesc[0] ? 'desc' : 'asc'
        }
        this.getWebhooksPage({
          skip: (this.currentPage - 1) * this.itemsPerPage,
          limit: this.itemsPerPage,
          sort: {
            by: this.sortBy,
            order: this.sortOrder,
          },
        })
      },
    },

    emptySearchResults() {
      return !this.hasItems && this.searchTerms
    },
  },

  mounted() {
    this.fetchFirstPage()
  },

  methods: {
    ...mapActions({
      getWebhookList: 'getWebhookList',
      deleteWebhook: 'deleteWebhook',
    }),

    humanDate(date) {
      return utils.dates.humanDate(date)
    },

    rowsClass() {
      return 'cursor-pointer'
    },

    shortenURL(url = '') {
      if (url.length <= MAX_URL_CHARACTERS) return url

      const firstSubstrTotalChars = MAX_URL_CHARACTERS / 2
      const secondSubstrTotalChars = firstSubstrTotalChars - 1
      const secondSubstrStart = url.length - secondSubstrTotalChars

      return `${url.slice(0, firstSubstrTotalChars)}...${url.slice(
        secondSubstrStart,
        url.length
      )}`
    },

    handleSearch(terms) {
      this.searchTerms = terms
      this.getWebhooksPage(this.options)
    },

    async getWebhooksPage(options) {
      try {
        this.isLoading = true

        const response = await this.getWebhookList({
          ...options,
          organizationId: this.organizationId,
          terms: this.searchTerms,
        })

        this.totalItems = response?.data?.totalRecordsInData ?? 0
        this.webhooks = response?.data?.results ?? []
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.isLoading = false
      }
    },

    toggleDeleteConfirmation(webhookId = '') {
      this.webhookIdToDelete = webhookId
      this.isDeleteConfirmationVisible = !this.isDeleteConfirmationVisible
    },

    async handleWebhookDeleted() {
      this.isDeleting = true

      try {
        await this.deleteWebhook({
          webhookId: this.webhookIdToDelete,
          organizationId: this.organizationId,
        })
        this.toggleDeleteConfirmation()
        this.fetchFirstPage()
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.isDeleting = false
      }
    },

    fetchFirstPage() {
      this.currentPage = 1

      this.getWebhooksPage({
        skip: 0,
        limit: this.itemsPerPage,
      })
    },

    getEnableStatusColor(enabled) {
      return enabled ? 'green' : 'grey'
    },

    getEnableStatusText(enabled) {
      return enabled ? this.$t('common.enabled') : this.$t('common.disabled')
    },
  },
}
</script>

<style lang="scss" scoped>
.empty-state {
  max-width: 60%;

  &__button {
    width: 129px;
  }
}
</style>
